/**
 * Chuông thông báo (BOSS 01/10/2026): số đỏ trên chuông, bấm ra danh sách thông báo,
 * bấm một dòng thì mở đúng màn. Cuối danh sách: bật thông báo về điện thoại (Web Push)
 * + hướng dẫn cài AGADS thành ứng dụng (Android Chrome / iPhone Safari).
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { goiApi } from '../lib/api';
import { cauLoi, dungThongBao } from './ThongBao';

interface MotThongBao {
  id: string;
  loai: string;
  mucDo: 'tin' | 'canh_bao' | 'loi';
  tieuDe: string;
  noiDung: string | null;
  lienKet: string | null;
  soLan: number;
  luc: string;
  daDoc: boolean;
}
interface DuLieuChuong {
  ok: true;
  ds: MotThongBao[];
  chuaDoc: number;
  soThietBi: number;
}

const MAU: Record<MotThongBao['mucDo'], string> = {
  tin: 'var(--agads-info-chu)',
  canh_bao: 'var(--agads-vang-chu)',
  loi: 'var(--agads-do-chu)',
};

function bao(luc: string): string {
  const giay = (Date.now() - Date.parse(luc)) / 1000;
  if (giay < 60) return 'vừa xong';
  if (giay < 3600) return `${Math.floor(giay / 60)} phút trước`;
  if (giay < 86400) return `${Math.floor(giay / 3600)} giờ trước`;
  return new Date(luc).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const laIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const daCaiApp = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true;

function base64UrlThanhMang(b64: string): Uint8Array {
  const dem = '='.repeat((4 - (b64.length % 4)) % 4);
  const chuoi = atob((b64 + dem).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(chuoi, (c) => c.charCodeAt(0));
}

/** Bật Web Push trên thiết bị này: xin quyền -> đăng ký service worker -> gửi đăng ký lên AGADS. */
async function batThongBaoDienThoai(): Promise<string> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return laIos() && !daCaiApp()
      ? 'iPhone chỉ nhận thông báo khi AGADS đã được thêm ra màn hình chính: mở bằng Safari › nút Chia sẻ › "Thêm vào MH chính", rồi mở AGADS từ biểu tượng đó và bật lại.'
      : 'Trình duyệt này không hỗ trợ thông báo đẩy — dùng Chrome (Android / máy tính) hoặc Safari iOS 16.4+.';
  }
  const quyen = await Notification.requestPermission();
  if (quyen !== 'granted') return 'Bạn chưa cho phép thông báo — bật lại trong cài đặt trình duyệt / điện thoại.';
  const dk = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  await navigator.serviceWorker.ready;
  const { khoa } = await goiApi<{ khoa: string }>('/thong-bao/push/khoa');
  const sub =
    (await dk.pushManager.getSubscription()) ??
    (await dk.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64UrlThanhMang(khoa) as BufferSource }));
  const j = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  const thietBi = `${laIos() ? 'iPhone/iPad' : /android/i.test(navigator.userAgent) ? 'Android' : 'Máy tính'}${daCaiApp() ? ' · ứng dụng' : ' · trình duyệt'}`;
  await goiApi('/thong-bao/push/dang-ky', { method: 'POST', than: { endpoint: j.endpoint, keys: j.keys, thietBi } });
  return 'Đã bật thông báo trên thiết bị này.';
}

export function ChuongThongBao() {
  const dieuHuong = useNavigate();
  const queryClient = useQueryClient();
  const { baoTot, baoLoi } = dungThongBao();
  const [mo, setMo] = useState(false);
  const [moHuongDan, setMoHuongDan] = useState(false);
  const khung = useRef<HTMLDivElement>(null);
  const { data } = useQuery({
    queryKey: ['thong-bao'],
    queryFn: () => goiApi<DuLieuChuong>('/thong-bao'),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
  const daDoc = useMutation({
    mutationFn: (than: { id?: string; tatCa?: true }) => goiApi('/thong-bao/da-doc', { method: 'POST', than }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['thong-bao'] }),
  });
  const batPush = useMutation({
    mutationFn: batThongBaoDienThoai,
    onSuccess: (tb) => {
      (tb.startsWith('Đã bật') ? baoTot : baoLoi)(tb);
      void queryClient.invalidateQueries({ queryKey: ['thong-bao'] });
    },
    onError: (e) => baoLoi(cauLoi(e)),
  });
  const thuPush = useMutation({
    mutationFn: () => goiApi<{ daGui: number }>('/thong-bao/push/thu', { method: 'POST', than: {} }),
    onSuccess: (d) => (d.daGui ? baoTot(`Đã gửi thử tới ${d.daGui} thiết bị`) : baoLoi('Chưa có thiết bị nào bật thông báo')),
    onError: (e) => baoLoi(cauLoi(e)),
  });

  // Bấm ra ngoài / Esc thì đóng.
  useEffect(() => {
    if (!mo) return;
    const ngoai = (e: MouseEvent) => {
      if (khung.current && !khung.current.contains(e.target as Node)) setMo(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setMo(false);
    document.addEventListener('mousedown', ngoai);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', ngoai);
      document.removeEventListener('keydown', esc);
    };
  }, [mo]);

  // Số trên biểu tượng app (Android / iOS khi đã cài) — trình duyệt nào có thì dùng.
  const chuaDoc = data?.chuaDoc ?? 0;
  useEffect(() => {
    const n = navigator as unknown as { setAppBadge?: (n: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
    if (chuaDoc > 0) void n.setAppBadge?.(chuaDoc).catch(() => undefined);
    else void n.clearAppBadge?.().catch(() => undefined);
  }, [chuaDoc]);

  return (
    <div ref={khung} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-label={`Thông báo${chuaDoc ? ` — ${chuaDoc} chưa đọc` : ''}`}
        onClick={() => setMo((x) => !x)}
        style={{
          position: 'relative',
          width: 36,
          height: 36,
          borderRadius: 10,
          border: '1px solid var(--agads-vien-2)',
          background: mo ? 'var(--agads-primary-nen)' : 'var(--agads-nen-trang)',
          color: 'var(--agads-muc-2)',
          cursor: 'pointer',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {chuaDoc > 0 && (
          <span
            style={{
              position: 'absolute',
              top: -6,
              right: -6,
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              borderRadius: 9,
              background: '#e5383b',
              color: '#fff',
              fontSize: 10.5,
              fontWeight: 700,
              lineHeight: '18px',
              textAlign: 'center',
              boxShadow: '0 0 0 2px var(--agads-nen-trang)',
            }}
          >
            {chuaDoc > 99 ? '99+' : chuaDoc}
          </span>
        )}
      </button>

      {mo && (
        <div
          role="dialog"
          aria-label="Danh sách thông báo"
          className="ag-chuong-khung"
          style={{
            position: 'absolute',
            right: 0,
            top: 44,
            width: 380,
            maxWidth: 'calc(100vw - 24px)',
            maxHeight: '70vh',
            overflowY: 'auto',
            background: 'var(--agads-nen-trang)',
            border: '1px solid var(--agads-vien)',
            borderRadius: 12,
            boxShadow: '0 12px 32px rgba(0,0,0,.16)',
            zIndex: 60,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid var(--agads-vien)', position: 'sticky', top: 0, background: 'var(--agads-nen-trang)' }}>
            <strong style={{ fontSize: 14 }}>Thông báo</strong>
            {chuaDoc > 0 && <span className="ag-mo" style={{ fontSize: 12, marginLeft: 6 }}>{chuaDoc} chưa đọc</span>}
            <span style={{ flex: 1 }} />
            {chuaDoc > 0 && (
              <button type="button" onClick={() => daDoc.mutate({ tatCa: true })} style={{ border: 'none', background: 'none', color: 'var(--agads-primary)', fontSize: 12.5, cursor: 'pointer' }}>
                Đọc hết
              </button>
            )}
          </div>
          {!data && <p className="ag-mo" style={{ padding: 12, fontSize: 13 }}>Đang tải…</p>}
          {data && data.ds.length === 0 && <p className="ag-mo" style={{ padding: 14, fontSize: 13, margin: 0 }}>Chưa có thông báo nào trong 30 ngày qua.</p>}
          {data?.ds.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                if (!t.daDoc) daDoc.mutate({ id: t.id });
                setMo(false);
                if (t.lienKet) dieuHuong(t.lienKet);
              }}
              style={{
                display: 'flex',
                gap: 9,
                width: '100%',
                textAlign: 'left',
                padding: '10px 12px',
                border: 'none',
                borderBottom: '1px solid var(--agads-vien)',
                background: t.daDoc ? 'transparent' : 'var(--agads-info-nen)',
                cursor: 'pointer',
                font: 'inherit',
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: 4, marginTop: 6, flex: 'none', background: t.daDoc ? 'transparent' : MAU[t.mucDo] }} />
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: 'block', fontSize: 13, fontWeight: t.daDoc ? 500 : 650, color: t.mucDo === 'tin' ? 'inherit' : MAU[t.mucDo] }}>
                  {t.tieuDe}
                  {t.soLan > 1 && <span className="ag-mo" style={{ fontWeight: 400 }}> · {t.soLan} lần</span>}
                </span>
                {t.noiDung && (
                  <span className="ag-mo" style={{ fontSize: 12, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' } as React.CSSProperties}>
                    {t.noiDung}
                  </span>
                )}
                <span className="ag-mo" style={{ display: 'block', fontSize: 11, marginTop: 3 }}>{bao(t.luc)}</span>
              </span>
            </button>
          ))}
          <div style={{ padding: '10px 12px', fontSize: 12.5, display: 'grid', gap: 6 }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <button type="button" disabled={batPush.isPending} onClick={() => batPush.mutate()} style={{ border: '1px solid var(--agads-primary)', background: 'var(--agads-primary)', color: '#fff', borderRadius: 8, padding: '6px 10px', fontSize: 12.5, cursor: 'pointer' }}>
                {batPush.isPending ? 'Đang bật…' : '📱 Bật thông báo trên thiết bị này'}
              </button>
              {!!data?.soThietBi && (
                <button type="button" disabled={thuPush.isPending} onClick={() => thuPush.mutate()} style={{ border: '1px solid var(--agads-vien-2)', background: 'var(--agads-nen-trang)', borderRadius: 8, padding: '6px 10px', fontSize: 12.5, cursor: 'pointer' }}>
                  Gửi thử
                </button>
              )}
              <span className="ag-mo" style={{ fontSize: 11.5 }}>{data?.soThietBi ? `${data.soThietBi} thiết bị đang nhận` : 'chưa có thiết bị nào'}</span>
            </div>
            <button type="button" onClick={() => setMoHuongDan((x) => !x)} style={{ border: 'none', background: 'none', padding: 0, textAlign: 'left', color: 'var(--agads-primary)', fontSize: 12, cursor: 'pointer' }}>
              {moHuongDan ? '▾' : '▸'} Cài AGADS thành ứng dụng trên điện thoại
            </button>
            {moHuongDan && (
              <div className="ag-mo" style={{ fontSize: 12, lineHeight: 1.6 }}>
                <div><strong>Android:</strong> mở agads.homepharma.vn bằng Chrome › menu ⋮ › <em>Cài đặt ứng dụng</em> (hoặc <em>Thêm vào màn hình chính</em>) → có biểu tượng AGADS. Mở từ biểu tượng rồi bấm "Bật thông báo".</div>
                <div><strong>iPhone / iPad (iOS 16.4+):</strong> mở bằng <strong>Safari</strong> › nút Chia sẻ <span aria-hidden>⬆</span> › <em>Thêm vào MH chính</em>. Mở AGADS từ biểu tượng mới đó rồi bấm "Bật thông báo" — iPhone chỉ nhận thông báo khi mở từ biểu tượng.</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
